import { MapboxDem } from "./MapboxDem";

const TILE_SIZE = 512;

/**
 * The `DigitalElevationModel` allows querying elevation for (lat, lng)
 * positions from the Mapbox DEM without a map.
 *
 * Usage:
 * Construct an instance with the horizontal resolution of the area of
 * interest, in cells. The vertical resolution is assumed to be in the same
 * order of magnitude. Also provide a width in degrees of longitude of the
 * area of interest:
 *
 *   new DigitalElevationModel(
 *     100,    // number of cells (horizontally) in the area
 *     0.1,   // area covers 0.1 degrees of longitude
 *   );
 *
 * This information is used to calculate the zoom level at which DEM tiles
 * must be downloaded.
 *
 * Tile fetching, sku-token capture, parent-tile fallback, and per-tile
 * caching all live in MapboxDem (shared with the LocalMap clipping path).
 * This class is now a thin wrapper that picks the right zoom level and
 * delegates the actual elevation lookup. The Mapbox access token and sku
 * are managed globally by MapboxDem — set by LocalMap at startup, with
 * the sku captured from passing tile requests.
 */
class DigitalElevationModel {
  private zoom: number;

  /**
   * @param resolution Horizontal resolution, in cells, of the area of interest, e.g. `100`
   * @param widthInDegrees Width in longitude degrees of the area of interest, e.g. `0.1`
   */
  constructor(resolution: number, widthInDegrees: number) {
    // Establish zoom level at which tiles must be retrieved. Cap at 14 to
    // match terrain-dem-v1's documented coverage; MapboxDem's parent
    // fallback would otherwise serve a coarser tile anyway.
    this.zoom = Math.min(14, this.calcZoom(resolution, widthInDegrees));
  }

  /**
   * Given a horizontal export cells resolution and an export rectangle
   * width in degrees longitude, returns a zoom level at which each cell
   * corresponds to a different pixel in DEM tiles.
   *
   * SAMPLE CALCULATION
   * Resolution    100  cells
   * minLng          1  deg
   * maxLng        1.5  deg
   * LngWidth      0.5  deg
   * LngPerCell  0.005
   *
   * Zoom  Tiles wide  Pixels wide    PixelsPerLngDegree    Pixels in cell
   * z       2^z         2^z * 512    2 ^ z * 512 / 360     / lngPerCell
   * 0         1               512          1.422222222     0.007111111
   * 1         2              1024          2.844444444     0.014222222
   * 2         4              2048          5.688888889     0.028444444
   * 3         8              4096          11.37777778     0.056888889
   * 4        16              8192          22.75555556     0.113777778
   * 5        32             16384          45.51111111     0.227555556
   * 6        64             32768          91.02222222     0.455111111
   * 7       128             65536          182.0444444     0.910222222
   * 8       256            131072          364.0888889     1.820444444
   * 9       512            262144          728.1777778     3.640888889
   *
   * @param resolution Horizontal area resolution in cells
   * @param widthInDegrees Area width in degrees of longitude
   * @returns Minimum zoom level
   */
  private calcZoom = (resolution: number, widthInDegrees: number): number => {
    const cellWidthInDegrees = widthInDegrees / resolution;
    let z = 0;
    let pixelsPerCell;
    do {
      pixelsPerCell = Math.pow(2, z) * TILE_SIZE / 360 * cellWidthInDegrees;
      z++;
    } while (pixelsPerCell < 1);

    return z;
  }

  /**
   * Query elevation in meters at (lng, lat) at this DEM's chosen zoom.
   * Returns null when no DEM tile is available — typically because no sku
   * token has been captured yet (the user hasn't interacted with the map).
   */
  public queryElevation = (lng: number, lat: number): Promise<number | null> => {
    return MapboxDem.getElevation(lng, lat, this.zoom);
  }

  /**
   * Retrieve the zoom level this DEM will run at.
   */
  public getZoom = (): number => {
    return this.zoom;
  }
}

export { DigitalElevationModel }
